import { useEffect } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateCrmMasterRequest, DeleteCrmMasterRequest } from "@/lib/validation/crm";
import { fetchGeneralApiGet, fetchGeneralApiPost } from "../utils";
import type {
  CrmMasterDetail,
  CrmMasterDetailTarget,
  CrmMasterListQuery,
  CrmMasterListResult,
  CrmMasterRef,
  DeleteCrmMasterResult,
} from "./types";

export type {
  CrmAddress,
  CrmAuthUserOrganization,
  CrmContact,
  CrmMasterDetail,
  CrmMasterDetailTarget,
  CrmMasterListQuery,
  CrmMasterListResult,
  CrmMasterSummary,
  CrmRecordAuthUser,
  CrmRecordDetail,
  CrmRecordSummary,
  CrmRecordType,
  CrmRecordTypeFieldName,
  CrmRecordTypeFields,
  CrmTag,
  CrmTagCategory,
  CrmTagType,
  DeleteCrmMasterResult,
} from "./types";

export const crmMastersQueryKey = ["general_api", "crm", "masters"] as const;

/** Server maximum page size for GET /crm/masters. */
const ALL_MASTERS_PAGE_SIZE = 500;

function toMasterListSearchParams(query: CrmMasterListQuery): URLSearchParams {
  const params = new URLSearchParams();
  const search = query.search?.trim();
  if (search) params.set("search", search);
  if (query.record_types?.length) params.set("record_types", query.record_types.join(","));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  return params;
}

export function listCrmMasters(query: CrmMasterListQuery = {}) {
  return fetchGeneralApiGet<CrmMasterListResult>(
    "crm/masters",
    toMasterListSearchParams(query),
  );
}

export function getCrmMaster(masterId: string) {
  return fetchGeneralApiGet<CrmMasterDetail>(`crm/masters/${encodeURIComponent(masterId)}`);
}

export function getCrmMasterByRecord(recordId: string) {
  return fetchGeneralApiGet<CrmMasterDetail>(`crm/records/${encodeURIComponent(recordId)}`);
}

/** The master containing the active record linked to the customer. */
export function getCrmMasterByCustomer(customerId: string) {
  return fetchGeneralApiGet<CrmMasterDetail>(`crm/records/by-customer/${encodeURIComponent(customerId)}`);
}

function getCrmMasterDetail(target: CrmMasterDetailTarget) {
  if ("record_id" in target) return getCrmMasterByRecord(target.record_id);
  if ("customer_id" in target) return getCrmMasterByCustomer(target.customer_id);
  return getCrmMaster(target.master_id);
}

export function useGetCrmMasterDetail(target: CrmMasterDetailTarget, enabled = true) {
  return useQuery({
    queryKey: [...crmMastersQueryKey, "detail", target],
    queryFn: async (): Promise<CrmMasterDetail> => {
      const response = await getCrmMasterDetail(target);
      if (!response.success) {
        throw new Error(response.message || "Unable to load CRM record");
      }
      return response.data;
    },
    enabled,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
}

/**
 * Loads every CRM master one page at a time. The first page is available as soon as it
 * arrives; each following page is requested only after the previous one finishes.
 * Loading stops on the first failed page; call `fetchNextPage` to resume from it.
 */
export function useListAllCrmMasters() {
  const result = useInfiniteQuery({
    queryKey: [...crmMastersQueryKey, "list", "all"],
    queryFn: async ({ pageParam = 0 }: { pageParam?: number }) => {
      const response = await listCrmMasters({ offset: pageParam, limit: ALL_MASTERS_PAGE_SIZE });
      if (!response.success) {
        throw new Error(response.message || "Unable to load CRM records");
      }
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    refetchOnWindowFocus: false,
  });

  const pageCount = result.data?.pages.length ?? 0;
  const { hasNextPage, isFetching, isError, fetchNextPage } = result;

  useEffect((): void => {
    if (pageCount > 0 && hasNextPage && !isFetching && !isError) {
      void fetchNextPage();
    }
  }, [pageCount, hasNextPage, isFetching, isError, fetchNextPage]);

  return result;
}

/** Creates a record group (master) with no records. */
export function createCrmMaster(request: CreateCrmMasterRequest) {
  return fetchGeneralApiPost<CrmMasterRef>("crm/masters", request);
}

export function useCreateCrmMaster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCrmMaster,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: crmMastersQueryKey });
    },
  });
}

/** Developer only: soft-deletes a master. The server refuses while it still has records, including removed ones. */
export function deleteCrmMaster(masterId: string, request: DeleteCrmMasterRequest) {
  return fetchGeneralApiPost<DeleteCrmMasterResult>(
    `crm/developer/masters/${encodeURIComponent(masterId)}/delete`,
    request,
  );
}

export function useDeleteCrmMaster(masterId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DeleteCrmMasterRequest) => deleteCrmMaster(masterId, request),
    // Refresh on every outcome: a revision conflict also needs the latest revision loaded.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: crmMastersQueryKey });
    },
  });
}
